import type { PdfPageSearchQuery } from '@affine/graphql';
import { nanoid } from 'nanoid';

import { DEFAULT_DOC_MODE } from '../../doc/constants';
import type { DocsService } from '../../doc/services/docs';
import type {
  Workbench,
  WorkbenchOpenOptions,
} from '../../workbench/entities/workbench';
import type { SearchSourceType } from './search-source';

export interface DocsBlockPayload {
  docId: string;
  title?: string;
  blockId?: string | undefined;
  blockContent?: string | undefined;
  sourceType?: SearchSourceType;
  /** Header rows can aggregate several source kinds under one doc result. */
  sourceTypes?: SearchSourceType[];
  elementId?: string | undefined;
  parentBlockId?: string | undefined;
  parentFlavour?: string | undefined;
  noteBlockId?: string | undefined;
  displayMode?: string | undefined;
  databaseId?: string | undefined;
  databaseRowId?: string | undefined;
  databaseRowShardDocId?: string | undefined;
  databaseColumnId?: string | undefined;
  visibility?: 'semantic' | 'technical';
  technicalSourceKind?: 'mail_storage';
}

export interface DocsPdfPagePayload {
  kind: 'pdf-page';
  docId: string;
  attachmentBlockId: string;
  pageIndex: number;
  query: string;
  blockContent: string;
  rects: PdfPageSearchHit['rects'];
}

export interface DocsPdfDocHeaderPayload {
  kind: 'pdf-doc-header';
  docId: string;
  attachmentBlockId: string;
}

export interface DocsPdfAttachmentHeaderPayload {
  kind: 'pdf-attachment-header';
  docId: string;
  attachmentBlockId: string;
}

export type DocsPayload =
  | DocsBlockPayload
  | DocsPdfPagePayload
  | DocsPdfDocHeaderPayload
  | DocsPdfAttachmentHeaderPayload;

export type PdfPageSearchHit =
  PdfPageSearchQuery['workspace']['pdfSearch'][number];

export interface DocsSearchResult {
  docId: string;
  title: string;
  score: number;
  sourceType: SearchSourceType;
  blockId?: string;
  elementId?: string;
  parentBlockId?: string;
  parentFlavour?: string;
  noteBlockId?: string;
  displayMode?: string;
  blockContent?: string;
  databaseId?: string;
  databaseRowId?: string;
  databaseRowShardDocId?: string;
  databaseColumnId?: string;
  databaseTitle?: string;
  databaseRowLabel?: string;
  databaseColumnName?: string;
  visibility?: 'semantic' | 'technical';
  technicalSourceKind?: 'mail_storage';
}

export interface AttachmentNavigation {
  kind: 'attachment';
  docId: string;
  attachmentBlockId: string;
  pageIndex: number;
  query: string;
  highlightRects: DocsPdfPagePayload['rects'];
}

export interface DocNavigation {
  kind: 'doc';
  docId: string;
  blockIds?: string[];
  elementIds?: string[];
  mode?: 'page' | 'edgeless';
  fullscreenDatabaseId?: string;
  fullscreenDatabaseRowId?: string;
  fullscreenDatabaseRowShardDocId?: string;
  fullscreenDatabaseColumnId?: string;
}

export type DocsSearchNavigation = AttachmentNavigation | DocNavigation;

export type DocsSearchBlockStatus = 'exists' | 'missing' | 'unknown';

export interface DocsSearchNavigationResolvers {
  /** The doc's primary mode; notes live in both page and edgeless mode. */
  resolveDocMode: (docId: string) => 'page' | 'edgeless';
  /** The id of the database a block lives in, or null if it is not in one. */
  resolveAncestorDatabaseId: (docId: string, blockId: string) => string | null;
  /**
   * Block existence in the currently loaded doc.
   *
   * `unknown` means the doc is not loaded; keep cross-doc navigation intact.
   * `missing` means the doc is loaded and the selected search row is stale.
   */
  resolveBlockStatus: (docId: string, blockId: string) => DocsSearchBlockStatus;
}

/** Navigable payload subset; recent-doc passes a bare `{ docId }`. */
export type DocsSearchNavigablePayload = {
  docId?: string;
  blockId?: string;
  elementId?: string;
  sourceType?: SearchSourceType;
  databaseId?: string;
  databaseRowId?: string;
  databaseRowShardDocId?: string;
  databaseColumnId?: string;
} & Partial<DocsPayload>;

/** Stable identity of the physical location a search result opens. */
export function getDocsSearchLocationIdentity(
  payload: DocsSearchNavigablePayload
): string {
  const docId = payload.docId ?? '';
  if (payload.sourceType === 'database' || payload.databaseId) {
    return [
      'database',
      docId,
      payload.databaseId ?? payload.blockId ?? '',
      payload.databaseRowId ?? '',
      payload.databaseColumnId ?? '',
    ].join('\u0000');
  }
  if (payload.sourceType === 'edgeless' || payload.elementId) {
    return ['edgeless', docId, payload.elementId ?? payload.blockId ?? ''].join(
      '\u0000'
    );
  }
  if (payload.blockId) {
    return ['block', docId, payload.blockId].join('\u0000');
  }
  return ['doc', docId].join('\u0000');
}

export interface OpenDocsSearchNavigationOptions extends WorkbenchOpenOptions {
  requestId?: string;
}

type DocsSearchDocOpenOptions = {
  docId: string;
  blockIds?: string[];
  elementIds?: string[];
  mode?: 'page' | 'edgeless';
  fullscreenDatabaseId?: string;
  fullscreenDatabaseRowId?: string;
  fullscreenDatabaseRowShardDocId?: string;
  fullscreenDatabaseColumnId?: string;
  refreshKey: string;
};

type LoadedBlock = NonNullable<
  ReturnType<DocsService['loaded']>
>['doc']['blockSuiteDoc']['getBlock'] extends (id: string) => infer R
  ? R
  : never;

export function isDocsPdfPagePayload(
  payload: DocsPayload | undefined
): payload is DocsPdfPagePayload {
  return !!payload && 'kind' in payload && payload.kind === 'pdf-page';
}

export function getDocsPayloadSourceTypes(
  payload: DocsPayload | undefined
): SearchSourceType[] {
  if (!payload) return [];
  if ('kind' in payload) {
    return payload.kind.startsWith('pdf-') ? ['pdf'] : [];
  }
  if (payload.sourceTypes?.length) {
    return [...new Set(payload.sourceTypes)];
  }
  if (payload.sourceType) return [payload.sourceType];
  return payload.docId && !payload.blockId ? ['doc'] : [];
}

export function createDocsSearchNavigationResolvers(
  docsService: DocsService
): DocsSearchNavigationResolvers {
  return {
    resolveDocMode: docId => getDocMode(docsService, docId),
    resolveAncestorDatabaseId: (docId, blockId) =>
      findAncestorDatabase(docsService, docId, blockId),
    resolveBlockStatus: (docId, blockId) =>
      resolveBlockStatus(docsService, docId, blockId),
  };
}

/**
 * Resolve where a source row should navigate. Returns `null` when there is
 * nothing to open (no docId) or a loaded block source is stale.
 */
export function buildDocsSearchNavigation(
  payload: DocsSearchNavigablePayload,
  resolvers: DocsSearchNavigationResolvers
): DocsSearchNavigation | null {
  if (isDocsPdfPagePayload(payload as DocsPayload)) {
    const pdf = payload as DocsPdfPagePayload;
    const status = resolvers.resolveBlockStatus(
      pdf.docId,
      pdf.attachmentBlockId
    );
    if (status === 'missing') return null;

    return {
      kind: 'attachment',
      docId: pdf.docId,
      attachmentBlockId: pdf.attachmentBlockId,
      pageIndex: pdf.pageIndex,
      query: pdf.query,
      highlightRects: pdf.rects,
    };
  }

  const docId = payload.docId;
  if (!docId) return null;

  const blockId = payload.blockId;
  if (!blockId) {
    return { kind: 'doc', docId };
  }

  if (requiresExistingLoadedBlock(payload)) {
    const status = resolvers.resolveBlockStatus(docId, blockId);
    if (status === 'missing') return null;
  }

  if (payload.sourceType === 'edgeless') {
    return payload.elementId
      ? {
          kind: 'doc',
          docId,
          elementIds: [payload.elementId],
          mode: 'edgeless',
        }
      : { kind: 'doc', docId, mode: 'edgeless' };
  }

  if (payload.sourceType === 'database') {
    const databaseId = payload.databaseId ?? blockId;
    if (payload.databaseRowId && !payload.databaseRowShardDocId) {
      throw new Error(
        `Database search result is missing its row shard locator: database=${databaseId} row=${payload.databaseRowId}`
      );
    }
    return {
      kind: 'doc',
      docId,
      fullscreenDatabaseId: databaseId,
      fullscreenDatabaseRowId: payload.databaseRowId,
      fullscreenDatabaseRowShardDocId: payload.databaseRowShardDocId,
      fullscreenDatabaseColumnId: payload.databaseColumnId,
    };
  }

  const databaseId = resolvers.resolveAncestorDatabaseId(docId, blockId);
  if (databaseId) {
    return { kind: 'doc', docId, fullscreenDatabaseId: databaseId };
  }

  return {
    kind: 'doc',
    docId,
    blockIds: [blockId],
    mode: resolvers.resolveDocMode(docId),
  };
}

export function openDocsSearchNavigation(
  workbench: Workbench,
  navigation: DocsSearchNavigation,
  options: OpenDocsSearchNavigationOptions = {}
): void {
  const { requestId = nanoid(8), ...openOptions } = options;

  if (navigation.kind === 'attachment') {
    workbench.openAttachment(navigation.docId, navigation.attachmentBlockId, {
      ...openOptions,
      navigation: {
        pageIndex: navigation.pageIndex,
        query: navigation.query,
        highlightRects: navigation.highlightRects,
        requestId,
      },
    });
    return;
  }

  const docOptions: DocsSearchDocOpenOptions = {
    docId: navigation.docId,
    blockIds: navigation.blockIds,
    elementIds: navigation.elementIds,
    mode: navigation.mode,
    fullscreenDatabaseId: navigation.fullscreenDatabaseId,
    fullscreenDatabaseRowId: navigation.fullscreenDatabaseRowId,
    fullscreenDatabaseRowShardDocId: navigation.fullscreenDatabaseRowShardDocId,
    fullscreenDatabaseColumnId: navigation.fullscreenDatabaseColumnId,
    refreshKey: requestId,
  };
  workbench.openDoc(docOptions, openOptions);
}

function requiresExistingLoadedBlock(payload: DocsSearchNavigablePayload) {
  return payload.sourceType !== 'edgeless';
}

function withLoadedBlock<T>(
  docsService: DocsService,
  docId: string,
  blockId: string,
  onUnknown: () => T,
  onLoaded: (block: LoadedBlock) => T
): T {
  const loaded = docsService.loaded(docId);
  if (!loaded) return onUnknown();
  try {
    return onLoaded(loaded.doc.blockSuiteDoc.getBlock(blockId));
  } finally {
    loaded.release();
  }
}

function findAncestorDatabase(
  docsService: DocsService,
  docId: string,
  blockId: string
) {
  return withLoadedBlock(
    docsService,
    docId,
    blockId,
    () => null,
    block => {
      let current = block?.model;
      while (current) {
        if (current.flavour === 'affine:database') return current.id;
        current = current.parent ?? undefined;
      }
      return null;
    }
  );
}

function resolveBlockStatus(
  docsService: DocsService,
  docId: string,
  blockId: string
): DocsSearchBlockStatus {
  return withLoadedBlock(
    docsService,
    docId,
    blockId,
    () => 'unknown',
    block => (block ? 'exists' : 'missing')
  );
}

function getDocMode(docsService: DocsService, docId: string) {
  return (
    docsService.list.doc$(docId).value?.primaryMode$.value ?? DEFAULT_DOC_MODE
  );
}
